import Nav from "components/layout/Nav";
import { MenuBar } from "components/layout/MenuBar";
import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchEnquiries, deleteEnquiry } from "Slices/enquiry.slice";
import { FiMail, FiPhone, FiSearch, FiTrash2 } from "react-icons/fi";
import { MdSupportAgent } from "react-icons/md";

const startOfToday = () => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date.getTime();
};

export default function EnquiryList() {
    const dispatch = useDispatch();
    const { enquiries, loading, error } = useSelector((state) => state.enquiry);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        dispatch(fetchEnquiries());
    }, [dispatch]);

    const deferredSearch = useDeferredValue(searchTerm.trim().toLowerCase());

    const filteredEnquiries = useMemo(() => {
        if (!deferredSearch) {
            return enquiries;
        }

        return enquiries.filter((item) =>
            [item.firstName, item.lastName, item.email, item.phone, item.message]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(deferredSearch))
        );
    }, [deferredSearch, enquiries]);

    const todayEnquiries = useMemo(
        () => enquiries.filter((item) => new Date(item.createdAt).getTime() >= startOfToday()).length,
        [enquiries]
    );

    const uniqueContacts = useMemo(
        () => new Set(enquiries.map((item) => item.email || item.phone).filter(Boolean)).size,
        [enquiries]
    );

    const latestTimestamp = useMemo(() => {
        if (!enquiries.length) {
            return "No enquiries yet";
        }

        return new Date(enquiries[0].createdAt).toLocaleString();
    }, [enquiries]);

    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this enquiry?")) {
            dispatch(deleteEnquiry(id));
        }
    };

    return (
        <>
            <Nav />
            <div className="row">
                <MenuBar />
                <main className="adminPageMain adminPageMain--slate">
                    <div className="adminPageShell enquiryWorkspace">
                        <section className="enquiryHero">
                            <div className="enquiryHero__copy">
                                <span className="editorPageEyebrow">Lead Inbox</span>
                                <h1>Enquiry Center</h1>
                                <p>Review every incoming enquiry, scan contact intent quickly, and clear handled requests from one admin workspace.</p>
                            </div>
                            <div className="enquiryHero__status">
                                <MdSupportAgent />
                                <div>
                                    <strong>Latest activity</strong>
                                    <span>{latestTimestamp}</span>
                                </div>
                            </div>
                        </section>

                        <section className="enquirySummaryGrid">
                            <article className="enquiryMetricCard">
                                <span>Total enquiries</span>
                                <strong>{enquiries.length}</strong>
                                <p>All captured contact requests.</p>
                            </article>
                            <article className="enquiryMetricCard">
                                <span>Received today</span>
                                <strong>{todayEnquiries}</strong>
                                <p>Fresh enquiries submitted since midnight.</p>
                            </article>
                            <article className="enquiryMetricCard">
                                <span>Unique contacts</span>
                                <strong>{uniqueContacts}</strong>
                                <p>Email or phone matches across the inbox.</p>
                            </article>
                        </section>

                        <section className="enquiryToolbar">
                            <label className="enquirySearchField">
                                <FiSearch />
                                <input
                                    type="search"
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                    placeholder="Search by name, phone, email, or message"
                                />
                            </label>
                            <div className="enquiryToolbar__meta">
                                <strong>{filteredEnquiries.length}</strong>
                                <span>matching enquiries</span>
                            </div>
                        </section>

                        {loading ? (
                            <section className="enquiryPanel">
                                <p className="tableEmptyState">Loading enquiries...</p>
                            </section>
                        ) : null}

                        {error ? (
                            <section className="enquiryPanel">
                                <p className="tableEmptyState">Error: {error}</p>
                            </section>
                        ) : null}

                        {!loading && !error ? (
                            <section className="enquiryPanel">
                                <div className="tableWrapper">
                                    <div className="tableContainer">
                                        <table className="enquiryTable">
                                            <thead>
                                                <tr>
                                                    <th>Received</th>
                                                    <th>Contact</th>
                                                    <th>Message</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredEnquiries.length ? (
                                                    filteredEnquiries.map((item) => (
                                                        <tr key={item._id}>
                                                            <td>
                                                                <div className="tableTimestamp">
                                                                    <strong>{new Date(item.createdAt).toLocaleDateString()}</strong>
                                                                    <span>{new Date(item.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="tablePrimaryCell">
                                                                    <strong>{`${item.firstName || ""} ${item.lastName || ""}`.trim() || "Unnamed enquiry"}</strong>
                                                                    <a href={`mailto:${item.email}`} className="tableInlineLink">
                                                                        <FiMail />
                                                                        <span>{item.email}</span>
                                                                    </a>
                                                                    <a href={`tel:${item.phone}`} className="tableInlineLink">
                                                                        <FiPhone />
                                                                        <span>{item.phone}</span>
                                                                    </a>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="tableMessageCell">
                                                                    <strong>Message</strong>
                                                                    <p>{item.message || "No message provided."}</p>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="tableActionStack">
                                                                    <button
                                                                        type="button"
                                                                        className="tableActionBtn tableActionBtn--delete"
                                                                        onClick={() => handleDelete(item._id)}
                                                                    >
                                                                        <FiTrash2 />
                                                                        <span>Delete</span>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={4}>
                                                            <div className="tableEmptyState">
                                                                {searchTerm ? "No enquiries match the current search." : "No enquiries available right now."}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </section>
                        ) : null}
                    </div>
                </main>
            </div>
        </>
    );
}
